import { htmlToString, SiteMapRoute } from '@get-mosh/msh-shared';
import { FilledContentRelationshipField, filter } from '@prismicio/client';
import Routes from 'src/constants/Routes';
import { linkResolver, createClient } from 'src/prismicio';
import { EXCLUDED_FILTER } from './blogPost';
import { FAQPage, WithContext } from 'schema-dts';

// Setting this outside of the function so that it should be the
// value at build time
const lastModifiedDate = new Date().toISOString();

const pages: Array<SiteMapRoute> = [
    {
        path: Routes.HOME,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 1,
    },
    {
        path: Routes.ED_LANDING_PAGE,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 1,
    },
    {
        path: Routes.PE_LANDING_PAGE,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 1,
    },
    {
        path: Routes.SKIN_LANDING_PAGE,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 1,
    },
    {
        path: Routes.MENTAL_HEALTH_LANDING_PAGE,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 1,
    },
    {
        path: Routes.ABOUT_US,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.CONTACT_US,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.HOW_IT_WORKS,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.OUR_DOCTORS,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.PRESS,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.PRICING,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.RESULTS,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.BLOG,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 0.8,
    },
    {
        path: Routes.CAREERS,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.OUR_THERAPISTS,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.THERAPY,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.PRIVACY_POLICY,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.5,
    },
    {
        path: Routes.TERMS_OF_USE,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.5,
    },
    {
        path: Routes.QUIZZES,
        lastModifiedDate,
        changeFrequency: 'weekly',
        priority: 0.8,
    },
    {
        path: Routes.NON_PRESCRIPTION,
        lastModifiedDate,
        changeFrequency: 'daily',
        priority: 0.8,
    },
    {
        path: Routes.LIBIDO_VITAMIN,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.HAIR_LIBIDO_BUNDLE,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.KEEP_VITAMIN,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.MINOXIDIL,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.SHAMPOO_CONDITIONER,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.SLEEP_VITAMIN,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.WEIGHT_LOSS_SHAKE,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.WEIGHT_LOSS_SHAKE_ONE,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.WEIGHT_LOSS_SHAKE_ONE_TIME,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.WEIGHT_LOSS_SHAKE_ONE_ONE_TIME,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.7,
    },
    {
        path: Routes.SKIN_ANTI_AGEING,
        lastModifiedDate,
        changeFrequency: 'monthly',
        priority: 0.5,
    },
];

export const getSitemapLinks = async (): Promise<Array<SiteMapRoute>> => {
    const indexes: Array<SiteMapRoute> = [...pages];
    const client = createClient();

    try {
        const categories = await client.getAllByType('category_page', {
            orderings: {
                field: 'document.first_publication_date',
                direction: 'desc',
            },
            fetch: ['category_page.title'],
        });

        categories.forEach((route) => {
            const path = linkResolver(
                route as unknown as FilledContentRelationshipField,
            );

            if (!path) return;

            indexes.push({
                path,
                lastModifiedDate: new Date(
                    route.last_publication_date,
                ).toISOString(),
                changeFrequency: 'weekly',
                priority: 0.7,
            });
        });

        const blogs = await client.getAllByType('blog_page', {
            orderings: {
                field: 'document.first_publication_date',
                direction: 'desc',
            },
            filters: [EXCLUDED_FILTER],
            fetch: ['blog_page.title', 'blog_page.parent'],
        });

        blogs.forEach((route) => {
            const path = linkResolver(
                route as unknown as FilledContentRelationshipField,
            );
            if (!path) return;

            indexes.push({
                path,
                lastModifiedDate: new Date(
                    route.last_publication_date,
                ).toISOString(),
                changeFrequency: 'weekly',
                priority: 0.5,
            });
        });

        const comparisons = await client.getAllByType('comparison_page', {
            orderings: {
                field: 'document.first_publication_date',
                direction: 'desc',
            },
            filters: [filter.at('my.performance_page.meta_indexed', true)],
            fetch: ['comparison_page.title', 'comparison_page.parent'],
        });

        comparisons.forEach((route) => {
            const path = linkResolver(
                route as unknown as FilledContentRelationshipField,
            );
            if (!path) return;

            indexes.push({
                path,
                lastModifiedDate: new Date(
                    route.last_publication_date,
                ).toISOString(),
                changeFrequency: 'weekly',
                priority: 0.7,
            });
        });

        const performance = await client.getAllByType('performance_page', {
            orderings: {
                field: 'document.first_publication_date',
                direction: 'desc',
            },
            filters: [filter.at('my.performance_page.meta_indexed', true)],
            fetch: ['performance_page.title'],
        });

        performance.forEach((route) => {
            const path = linkResolver(
                route as unknown as FilledContentRelationshipField,
            );

            if (!path) return;

            indexes.push({
                path,
                lastModifiedDate: new Date(
                    route.last_publication_date,
                ).toISOString(),
                changeFrequency: 'weekly',
                priority: 0.7,
            });
        });

        const doctors = await client.getAllByType('doctor_page', {
            orderings: {
                field: 'document.first_publication_date',
                direction: 'desc',
            },
            fetch: ['doctor_page.name'],
        });

        doctors.forEach((route) => {
            const path = linkResolver(
                route as unknown as FilledContentRelationshipField,
            );
            if (!path) return;

            indexes.push({
                path,
                lastModifiedDate: new Date(
                    route.last_publication_date,
                ).toISOString(),
                changeFrequency: 'weekly',
                priority: 0.5,
            });
        });
    } catch {
        return [];
    }

    return indexes;
};

type FAQItem = {
    question: string | null;
    answer: string | null;
};

export const getSEOFaqMarkup = (
    data: FAQItem[],
): WithContext<FAQPage> | null => {
    const filteredData = data.filter(
        ({ question, answer }) => question && answer,
    );

    if (filteredData.length === 0) return null;

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: filteredData.map((item) => ({
            '@type': 'Question',
            name: item.question ?? undefined,
            acceptedAnswer: {
                '@type': 'Answer',
                text: htmlToString(item.answer as string) ?? undefined,
            },
        })),
    };
};
